import { numFont } from '../../utils/fonts';
import * as React from 'react';
import { Box, Button, TextField, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';

/**
 * A colour field: a swatch that opens the native colour picker, a row of presets and a hex
 * field for an exact value.
 *
 * The native input is uncontrolled and paints the swatch through the DOM while the user drags
 * inside the picker; React state is only updated on the input's `change` event. Without that
 * every pointer move would re-render the whole settings tab.
 */

export const PRESET_COLORS = [
  '#ef4444', // red
  '#f97316', // orange
  '#f59e0b', // amber
  '#eab308', // yellow
  '#84cc16', // lime
  '#10b981', // emerald
  '#14b8a6', // teal
  '#0ea5e9', // sky
  '#3b82f6', // blue
  '#8b5cf6', // violet
  '#ec4899', // pink
  '#64748b', // slate
];

const HEX_RE = /^#[0-9a-fA-F]{6}$/;

function ColorSwatchInput({
  value,
  onChange,
  presets = PRESET_COLORS,
  showPresets = true,
  showHex = true,
  size = 32,
  disabled = false,
  testIdPrefix = 'color',
  onReset,
  resetLabel,
}) {
  const { t } = useTranslation();
  const inputRef = React.useRef(null);
  const swatchId = `${testIdPrefix}-swatch`;
  const [hexDraft, setHexDraft] = React.useState(value || '');
  const [hexError, setHexError] = React.useState(false);

  // Commit only on the native change event: onInput fires continuously while dragging.
  React.useEffect(() => {
    const el = inputRef.current;
    if (!el) return undefined;
    const handler = (e) => onChange(e.target.value.toLowerCase());
    el.addEventListener('change', handler);
    return () => el.removeEventListener('change', handler);
  }, [onChange]);

  // Follow external changes (preset click, reset, another row's save).
  React.useEffect(() => {
    setHexDraft(value || '');
    setHexError(false);
    if (inputRef.current && value) {
      inputRef.current.value = value;
    }
    const swatch = document.getElementById(swatchId);
    if (swatch && value) {
      swatch.style.backgroundColor = value;
    }
  }, [value, swatchId]);

  const commitHex = () => {
    if (HEX_RE.test(hexDraft)) {
      setHexError(false);
      onChange(hexDraft.toLowerCase());
    } else {
      setHexError(true);
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
        <Box
          id={swatchId}
          data-testid={swatchId}
          onClick={() => !disabled && inputRef.current?.click()}
          sx={{
            width: size,
            height: size,
            borderRadius: 1,
            border: '2px solid',
            borderColor: 'divider',
            backgroundColor: value || '#ffffff',
            position: 'relative',
            overflow: 'hidden',
            flexShrink: 0,
            cursor: disabled ? 'default' : 'pointer',
          }}
        >
          <input
            ref={inputRef}
            type="color"
            disabled={disabled}
            defaultValue={value || '#000000'}
            data-testid={`${testIdPrefix}-input`}
            onInput={(e) => {
              // Paint during the drag without re-rendering.
              const swatch = document.getElementById(swatchId);
              if (swatch) swatch.style.backgroundColor = e.target.value;
            }}
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              opacity: 0,
              cursor: disabled ? 'default' : 'pointer',
            }}
          />
        </Box>

        {showHex && (
          <TextField
            size="small"
            value={hexDraft}
            disabled={disabled}
            error={hexError}
            helperText={hexError ? t('calendarSettings.color.invalidHex') : ''}
            onChange={(e) => setHexDraft(e.target.value)}
            onBlur={commitHex}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                commitHex();
              }
            }}
            inputProps={{
              maxLength: 7,
              'data-testid': `${testIdPrefix}-hex`,
              style: { ...numFont, fontSize: '13px', width: 90 },
            }}
          />
        )}

        {onReset && (
          <Button size="small" variant="text" onClick={onReset} data-testid={`${testIdPrefix}-reset`}>
            {resetLabel || t('calendarSettings.stateColors.reset')}
          </Button>
        )}
      </Box>

      {showPresets && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
          <Typography variant="caption" color="text.secondary" sx={{ mr: 0.5 }}>
            {t('calendarSettings.color.presets')}
          </Typography>
          {presets.map((preset) => (
            <Box
              key={preset}
              data-testid={`${testIdPrefix}-preset-${preset.replace('#', '')}`}
              onClick={() => !disabled && onChange(preset)}
              sx={{
                width: 20,
                height: 20,
                borderRadius: '4px',
                bgcolor: preset,
                cursor: disabled ? 'default' : 'pointer',
                border: '2px solid',
                borderColor: (value || '').toLowerCase() === preset ? 'text.primary' : 'transparent',
              }}
            />
          ))}
        </Box>
      )}
    </Box>
  );
}

export default ColorSwatchInput;
